import React from 'react'
import PropTypes from 'prop-types'
import Checkbox from '@material-ui/core/Checkbox'
import Link from '@material-ui/core/Link'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import useStyles from './styles'

/**
 * Privacy Policy / Terms of Service consent line. `MarketingOptIn` renders it
 * under the opt-in copy unless `showTermsNotice` is false, in which case the
 * form must render this next to its submit button instead.
 */
export function MarketingOptInTermsNotice({ className }) {
  const classes = useStyles({ stackCallout: false })
  return (
    <>
      {/* eslint-disable react/no-unescaped-entities */}
      <Typography
        variant="body2"
        className={className ? `${classes.proceedingText} ${className}` : classes.proceedingText}
      >
        By proceeding, you agree to PGA of America's{' '}
        <Link
          className={classes.link}
          href="https://www.pga.com/pga-of-america/privacy-policy"
          target="_blank"
        >
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link
          className={classes.link}
          href="https://www.pga.com/pga-of-america/terms-of-service"
          target="_blank"
        >
          Terms of Service
        </Link>
        .
      </Typography>
      {/* eslint-enable react/no-unescaped-entities */}
    </>
  )
}

MarketingOptInTermsNotice.propTypes = {
  className: PropTypes.string,
}

function MarketingOptIn({
  checked, onChange, useMarketingOptIn, stackCallout = false, showTermsNotice = true,
}) {
  const classes = useStyles({ stackCallout })

  const optInText = () => (
    <>
      {useMarketingOptIn === 'standard' && (
        <Typography variant="body2" className={classes.standardHeader}>
          Tee me up with PGA emails about events and tickets, swing tips, local golf programs, exclusive offers and more!
        </Typography>
      )}
      {/* eslint-disable react/no-unescaped-entities -- legal copy */}
      <Typography variant="body2" className={classes.optInText}>
        By checking this box, I confirm my request to be contacted by email by
        The Professional Golfers' Association of America ("PGA"), PGA Tournament
        Corporation, Inc. ("PTC") and/or PGA of America partners. I acknowledge
        that I can withdraw my consent at any time by following the
        'unsubscribe' instructions provided on any communication I receive from
        this service.
      </Typography>
      {/* eslint-enable react/no-unescaped-entities */}
    </>
  )

  const renderCalloutVariant = () => (
    <div className={classes.calloutContainer} data-testid="marketing-opt-in-callout">
      <div className={classes.calloutText}>
        <div className={classes.topSection}>
          <img
            className={classes.iconWithDivider}
            src="/images/logo-blue.svg"
            alt="PGA logo"
            width={32}
            height={32}
          />
          <Typography variant="h6" className={classes.header}>
            We Love This Game. How About You?
          </Typography>
        </div>
        {/* eslint-disable react/no-unescaped-entities */}
        <Typography variant="body2" className={classes.body}>
          Join the PGA of America's email community to hear about local golf
          programs, ticketing and volunteer opportunities, swing tips, exclusive
          offers and more!
        </Typography>
        {/* eslint-enable react/no-unescaped-entities */}
      </div>
      <Divider orientation="vertical" className={classes.middleDivider} />
      <div className={classes.calloutCheckbox}>
        <div className={classes.checkboxRow}>
          <Checkbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className={classes.checkbox}
          />
          <div className={classes.checkboxTexts}>
            {optInText()}
            {showTermsNotice ? <MarketingOptInTermsNotice /> : null}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className={classes.root}>
      {useMarketingOptIn === 'callout' ? renderCalloutVariant() : (
        <div className={classes.checkboxRow}>
          <Checkbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className={classes.checkbox}
          />
          <div className={classes.checkboxTexts}>
            {optInText()}
            {showTermsNotice ? <MarketingOptInTermsNotice /> : null}
          </div>
        </div>
      )}
    </div>
  )
}

MarketingOptIn.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  useMarketingOptIn: PropTypes.oneOf(['standard', 'callout']),
  stackCallout: PropTypes.bool,
  /** False when the form renders `MarketingOptInTermsNotice` by its submit button. */
  showTermsNotice: PropTypes.bool,
}

export default MarketingOptIn
